/**
 * CompositionPickerOverlay — `OPEN COMPOSITION ›` modal over the dimmed
 * ambient Global Intelligence canvas.
 *
 * Cycle FE-1 sprint-236 (Task 236.4, SDD §4.1 REVISION 2, design frames
 * `Global Intelligence 4/5 (environment).png`). Lists recent scoped
 * compositions from `GET /api/v1/theatres/` with status chips and
 * All/Research/Theatres/Active filters, plus a "NEW COMPOSITION" row that
 * POSTs `/api/v1/environment/compositions {preset: null}` and navigates to
 * the new build canvas.
 *
 * INVARIANTS:
 * - NO Global Intelligence card/row inside the picker (frame 1 rejected;
 *   ambient Global Intelligence is what you RETURN to by closing this).
 * - Token-clean: `var(--e-*)` only — no hardcoded oklch/hex.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiClient } from '../../api/client';
import { theatreApi } from '../../api/theatres';
import type { TheatreResponse, TheatreState } from '../../types/theatre';

// ── Status chips (theatre state → operator-facing chip, per frames 4/5) ──

type ChipLabel = 'ACTIVE' | 'EVALUATING' | 'VERIFIED' | 'DRAFT' | 'ARCHIVED';

const CHIP_FROM_STATE: Record<TheatreState, ChipLabel> = {
  DRAFT: 'DRAFT',
  MATERIALIZED: 'DRAFT',
  COMMITTED: 'ACTIVE',
  ACTIVE: 'ACTIVE',
  SETTLING: 'EVALUATING',
  RESOLVED: 'VERIFIED',
  ARCHIVED: 'ARCHIVED',
};

const CHIP_CLASSES: Record<ChipLabel, string> = {
  ACTIVE: 'text-[var(--e-green-600)] border-[var(--e-green-600)]',
  EVALUATING: 'text-[var(--e-orange-600)] border-[var(--e-orange-600)]',
  VERIFIED: 'text-[var(--e-purple-400)] border-[var(--e-purple-400)]',
  DRAFT: 'text-[var(--e-text-muted)] border-[var(--e-border-primary)]',
  ARCHIVED: 'text-[var(--e-text-disabled)] border-[var(--e-border-secondary)]',
};

function chipFor(state: TheatreState): ChipLabel {
  return CHIP_FROM_STATE[state] ?? 'DRAFT';
}

// ── Filters ──

type FilterId = 'all' | 'research' | 'theatres' | 'active';

const FILTERS: Array<{ id: FilterId; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'research', label: 'Research' },
  { id: 'theatres', label: 'Theatres' },
  { id: 'active', label: 'Active' },
];

const RESEARCH_INQUIRY_CLASSES = new Set(['INVESTIGATIVE', 'SURVEY', 'SCRUTINY']);

function matchesFilter(theatre: TheatreResponse, filter: FilterId): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'research':
      return RESEARCH_INQUIRY_CLASSES.has(theatre.inquiry_class ?? '');
    case 'theatres':
      return !RESEARCH_INQUIRY_CLASSES.has(theatre.inquiry_class ?? '');
    case 'active':
      return chipFor(theatre.state) === 'ACTIVE';
  }
}

function relativeAge(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

// ── Component ──

export interface CompositionPickerOverlayProps {
  open: boolean;
  onClose: () => void;
  /** Selecting a scoped composition — page overlays it on the ambient canvas. */
  onSelect: (theatre: TheatreResponse) => void;
}

export function CompositionPickerOverlay({
  open,
  onClose,
  onSelect,
}: CompositionPickerOverlayProps) {
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState<FilterId>('all');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['theatres', 'list', 'composition-picker'],
    queryFn: () => theatreApi.listTheatres({ limit: 20 }),
    enabled: open,
    staleTime: 30_000,
    retry: false,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const { data: created } = await apiClient.post<{ theatre_id: string }>(
        '/api/v1/environment/compositions',
        { preset: null },
      );
      return created;
    },
    onSuccess: (created) => {
      onClose();
      navigate(`/theatres/${created.theatre_id}/build`);
    },
  });

  // Esc closes; focus the dialog on open (matches SearchModal convention —
  // no formal focus-trap primitive exists in this codebase).
  useEffect(() => {
    if (!open) return;
    setFilter('all');
    const t = setTimeout(() => dialogRef.current?.focus(), 50);
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  const theatres = useMemo(
    () => (data?.theatres ?? []).filter((t) => matchesFilter(t, filter)),
    [data, filter],
  );

  if (!open) return null;

  return (
    // Dimmed backdrop over the ambient canvas (frames 4/5).
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-[2px]"
      data-testid="composition-picker-backdrop"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Open composition"
        tabIndex={-1}
        data-testid="composition-picker"
        className="w-[440px] max-h-[70vh] flex flex-col rounded-lg border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] shadow-[var(--e-shadow-md)] outline-none overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 pt-3 pb-2 border-b border-[var(--e-border-secondary)]">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--e-text-primary)]">
                Open Composition
              </div>
              <p className="text-[9px] text-[var(--e-text-muted)] mt-1">
                Select a scoped graph to overlay the global canvas. Close it to
                return to Global Intelligence.
              </p>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="text-[var(--e-text-muted)] hover:text-[var(--e-text-primary)] mt-0.5"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Filters */}
          <div
            className="flex items-center gap-1 mt-2"
            role="group"
            aria-label="Filter compositions"
          >
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                aria-pressed={filter === f.id}
                className={cn(
                  'px-2 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider transition-colors border',
                  filter === f.id
                    ? 'text-[var(--e-text-primary)] bg-[var(--e-bg-hover)] border-[var(--e-border-primary)]'
                    : 'text-[var(--e-text-muted)] border-transparent hover:text-[var(--e-text-secondary)]',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Recent compositions — NO Global Intelligence card here (frame 1 rejected). */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 pt-2 pb-1 text-[8px] font-mono uppercase tracking-widest text-[var(--e-text-muted)]/70">
            Recent compositions
          </div>
          <ul data-testid="composition-list" className="px-2 pb-1">
            {isLoading && (
              <li className="px-2 py-3 text-[10px] font-mono text-[var(--e-text-muted)]">
                Loading compositions…
              </li>
            )}
            {isError && (
              <li className="px-2 py-3 text-[10px] font-mono text-[var(--e-text-muted)]">
                Compositions unavailable.
              </li>
            )}
            {!isLoading && !isError && theatres.length === 0 && (
              <li className="px-2 py-3 text-[10px] font-mono text-[var(--e-text-muted)]">
                No compositions yet.
              </li>
            )}
            {theatres.map((t) => {
              const chip = chipFor(t.state);
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(t)}
                    data-testid={`composition-row-${t.id}`}
                    className="w-full flex items-center gap-2 px-2 py-2 rounded text-left transition-colors hover:bg-[var(--e-bg-hover)]"
                  >
                    <span
                      className={cn(
                        'text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border flex-shrink-0',
                        CHIP_CLASSES[chip],
                      )}
                    >
                      {chip}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] text-[var(--e-text-primary)] truncate">
                        {t.construct_id || t.id.slice(0, 8)}
                      </span>
                      <span className="block text-[9px] font-mono text-[var(--e-text-muted)]">
                        {(t.inquiry_class ?? 'COMPOSITION').toLowerCase()} ·{' '}
                        {relativeAge(t.updated_at)}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* New composition */}
        <div className="border-t border-[var(--e-border-secondary)] px-2 py-2">
          <button
            type="button"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
            data-testid="new-composition-row"
            className="w-full flex items-center gap-2 px-2 py-2 rounded text-left transition-colors hover:bg-[var(--e-bg-hover)] disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5 text-[var(--e-text-secondary)] flex-shrink-0" />
            <span>
              <span className="block text-[10px] font-mono uppercase tracking-wider text-[var(--e-text-primary)]">
                {createMutation.isPending ? 'Creating…' : 'New composition'}
              </span>
              <span className="block text-[9px] text-[var(--e-text-muted)]">
                start an empty graph
              </span>
            </span>
          </button>
          {createMutation.isError && (
            <p
              role="status"
              className="px-2 pt-1 text-[9px] font-mono text-[var(--e-red-600)]"
            >
              Could not create composition. Please try again.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
